import { EventEmitter } from "events";
import EditorError from "components/organisms/editor/core/error";
import RangesTracker from "components/organisms/editor/core/ranges";

import EditorWatchdogManager from "components/organisms/manuscript/overleaf/ot/editorWatchdogManager";
import { ShareJsDoc } from "components/organisms/manuscript/overleaf/sharejs/shareJsDoc";
import { debugConsole, getMeta } from "components/organisms/manuscript/overleaf/sharejs/vendor/support";

const MAX_PENDING_OP_SIZE = 64;

function isInsertOperation(op) {
    return typeof op?.i === "string";
}

function isDeleteOperation(op) {
    return typeof op?.d === "string";
}

function isCommentOperation(op) {
    return op?.c != null || op?.commentId != null || op?.t === "comment";
}

function isEditOperation(op) {
    return isInsertOperation(op) || isDeleteOperation(op);
}

function decodeUtf8(value) {
    return typeof value === "string" ? value : value;
}

function getOpSize(op) {
    if (typeof op?.i === "string") {
        return op.i.length;
    }

    if (typeof op?.d === "string") {
        return op.d.length;
    }

    return 0;
}

function getShareJsOpSize(shareJsOp) {
    return shareJsOp.reduce((total, op) => total + getOpSize(op), 0);
}

function resolveDocumentContainerOptions(maybeOptions, maybeIdeEventEmitter, maybeDetachDoc) {
    if (
        maybeOptions == null ||
        (typeof maybeOptions === "object" &&
            !Array.isArray(maybeOptions) &&
            (Object.prototype.hasOwnProperty.call(maybeOptions, "type") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "maxDocLength") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "detachDoc") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "onProjectJoined") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "globalEditorWatchdogManager") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "ideEventEmitter")))
    ) {
        return maybeOptions || {};
    }

    return {
        globalEditorWatchdogManager: maybeOptions,
        ideEventEmitter: maybeIdeEventEmitter,
        detachDoc: maybeDetachDoc,
    };
}

function createIdeEventEmitter(ideEventEmitter) {
    return ideEventEmitter || new EventEmitter();
}

function createGlobalEditorWatchdogManager(globalEditorWatchdogManager) {
    if (globalEditorWatchdogManager) {
        return globalEditorWatchdogManager;
    }

    return new EditorWatchdogManager({
        onTimeoutHandler: (meta) => {
            console.error("[manuscript-ot] editor watchdog timeout", meta);
        },
    });
}

export class DocumentContainer extends EventEmitter {
    constructor(docId, socket, maybeOptions = {}, maybeIdeEventEmitter, maybeDetachDoc) {
        super();
        const options = resolveDocumentContainerOptions(maybeOptions, maybeIdeEventEmitter, maybeDetachDoc);
        this.doc_id = docId;
        this.socket = socket;
        this.type = options.type || "history-ot";
        this.maxDocLength = options.maxDocLength;
        this.globalEditorWatchdogManager = createGlobalEditorWatchdogManager(options.globalEditorWatchdogManager);
        this.ideEventEmitter = createIdeEventEmitter(options.ideEventEmitter);
        this.connected = this.socket?.socket?.connected ?? false;
        this.wantToBeJoined = false;
        this.joinCallbacks = [];
        this.leaveCallbacks = [];
        this.joinLeaveEpoch = 0;
        this.joined = false;
        this.doc = null;
        this.ranges = null;
        this.track_changes_as = null;
        this.cm6 = null;
        this.cm6TrackChangesTimer = null;
        this.oldInflightOp = null;
        this.detachDoc = options.detachDoc || (() => {});
        this.onProjectJoined = options.onProjectJoined;
        this.docName = "";
        this.docChangedTimeout = null;
        this.docSavedTimeout = null;
        this.chaosMonkeyTimer = null;

        this.onUpdateAppliedHandler = (update) => this.onUpdateApplied(update);
        this.onErrorHandler = (error, message = {}) => {
            if (message?.doc_id != null && String(message.doc_id) !== String(this.doc_id)) {
                return;
            }
            this.onError(error, message);
        };
        this.onDisconnectHandler = () => this.onDisconnect();
        this.onReconnectHandler = () => this.onReconnect();

        this.bindToSocketEvents();
        this.bindToEditorEvents();
    }

    trigger(...args) {
        return this.emit(...args);
    }

    get shareDoc() {
        if (!this.doc?._doc) {
            throw new Error("Missing ShareJS Doc");
        }
        return this.doc._doc;
    }

    get historyOTShareDoc() {
        if (!this.isHistoryOT()) {
            throw new Error("shareDoc is not historyOT");
        }
        return this.shareDoc;
    }

    isHistoryOT() {
        return (this.doc?.getType?.() || this.type) === "history-ot";
    }

    attachToCM6(cm6) {
        if (this.cm6 && this.cm6 !== cm6) {
            this.detachFromCM6(this.cm6);
        }
        this.cm6 = cm6;
        if (this.doc) {
            this.doc.attachToCM6(cm6);
        }
        this.scheduleTrackChangesUserIdSync(cm6);
        this.cm6.on("change", this.checkConsistency);
    }

    detachFromCM6(cm6 = this.cm6) {
        if (!cm6 || this.cm6 !== cm6) {
            return;
        }
        if (this.cm6TrackChangesTimer != null) {
            window.clearTimeout(this.cm6TrackChangesTimer);
            this.cm6TrackChangesTimer = null;
        }
        if (this.doc) {
            this.doc.detachFromCM6();
        }
        if (cm6) {
            cm6.off("change", this.checkConsistency);
        }
        this.cm6 = null;
        this.clearChaosMonkey();
        if (this.doc) {
            this.ideEventEmitter?.emit?.("document:closed", this.doc);
        }
    }

    checkConsistency = (editor) => {
        window.setTimeout(() => {
            const editorValue = editor?.getValue();
            const sharejsValue = this.doc?.getSnapshot();
            if (editorValue !== sharejsValue) {
                this.onError(new Error("Editor text does not match server text"), {}, editorValue);
            }
        }, 0);
    };

    getSnapshot() {
        return this.doc?.getSnapshot();
    }

    getRawSnapshot() {
        return this.doc?.getRawSnapshot();
    }

    getType() {
        return this.doc?.getType() || this.type;
    }

    getVersion() {
        return this.doc?.getVersion();
    }

    getRanges() {
        if (this.isHistoryOT()) {
            const rawSnapshot = this.doc?.getRawSnapshot?.();
            return {
                changes: Array.isArray(rawSnapshot?.trackedChanges) ? rawSnapshot.trackedChanges : [],
                comments: Array.isArray(rawSnapshot?.comments) ? rawSnapshot.comments : [],
            };
        }

        if (this.ranges instanceof RangesTracker) {
            return {
                changes: Array.isArray(this.ranges.changes) ? this.ranges.changes : [],
                comments: Array.isArray(this.ranges.comments) ? this.ranges.comments : [],
            };
        }

        return null;
    }

    setTrackChangesUserId(userId) {
        this.track_changes_as = userId || null;
        this.doc?.setTrackChangesUserId(this.track_changes_as);
        if (this.ranges instanceof RangesTracker) {
            this.ranges.track_changes = !!this.track_changes_as;
        }
        this.scheduleTrackChangesUserIdSync();
    }

    scheduleTrackChangesUserIdSync(cm6 = this.cm6) {
        if (!cm6?.setTrackChangesUserId) {
            return;
        }
        if (this.cm6TrackChangesTimer != null) {
            window.clearTimeout(this.cm6TrackChangesTimer);
        }
        this.cm6TrackChangesTimer = window.setTimeout(() => {
            this.cm6TrackChangesTimer = null;
            if (!cm6?.setTrackChangesUserId || this.cm6 !== cm6) {
                return;
            }
            cm6.setTrackChangesUserId(this.track_changes_as);
        }, 0);
    }

    setTrackChangesIdSeeds(idSeeds) {
        if (!this.doc) {
            return;
        }
        this.doc.track_changes_id_seeds = idSeeds || null;
    }

    getTrackChangesEnabled() {
        return !!this.doc?.track_changes;
    }

    getTrackingChanges() {
        return !!this.doc?.track_changes;
    }

    getInflightOp() {
        return this.doc?.getInflightOp();
    }

    getPendingOp() {
        return this.doc?.getPendingOp();
    }

    getRecentAck() {
        return this.doc?.getRecentAck();
    }

    getInflightOpCreatedAt() {
        return this.doc?.getInflightOpCreatedAt();
    }

    getPendingOpCreatedAt() {
        return this.doc?.getPendingOpCreatedAt();
    }

    hasBufferedOps() {
        return this.doc?.hasBufferedOps();
    }

    submitOp(...ops) {
        this.doc?.submitOp(ops);
    }

    flush() {
        return this.doc?.flushPendingOps();
    }

    chaosMonkey(line = 0, char = "a") {
        const orig = char;
        let copy = null;
        let pos = 0;

        const timer = () => {
            if (copy == null || !copy.length) {
                copy = `${orig.slice()} ${new Date()}\n`;
                line += Math.random() > 0.1 ? 1 : -2;
                if (line < 0) {
                    line = 0;
                }
                pos = 0;
            }

            char = copy[0];
            copy = copy.slice(1);

            if (this.cm6) {
                this.cm6.view.dispatch({
                    changes: {
                        from: Math.min(pos, this.cm6.view.state.doc.length),
                        insert: char,
                    },
                });
            }

            pos += 1;
            this.chaosMonkeyTimer = window.setTimeout(timer, 100 + (Math.random() < 0.1 ? 1000 : 0));
        };

        timer();
    }

    clearChaosMonkey() {
        const timer = this.chaosMonkeyTimer;
        if (timer) {
            this.chaosMonkeyTimer = null;
            window.clearTimeout(timer);
        }
    }

    join(callback) {
        this.wantToBeJoined = true;
        this.cancelLeave();
        const epoch = ++this.joinLeaveEpoch;

        if (this.connected) {
            this.joinDoc(callback, epoch);
        } else if (callback) {
            this.joinCallbacks.push(callback);
        }
    }

    leave(callback) {
        this.flush();
        this.wantToBeJoined = false;
        this.cancelJoin();
        this.joinLeaveEpoch += 1;

        if (this.doc?.hasBufferedOps()) {
            if (callback) {
                this.leaveCallbacks.push(callback);
            }
            return;
        }

        if (!this.connected) {
            callback?.();
            return;
        }

        this.leaveDoc(callback);
    }

    leaveAndCleanUp(callback) {
        return this.leave((error) => {
            this.cleanUp();
            callback?.(error);
        });
    }

    leaveAndCleanUpPromise() {
        return new Promise((resolve, reject) => {
            this.leaveAndCleanUp((error) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve();
            });
        });
    }

    pollSavedStatus() {
        const inflightOp = this.getInflightOp();
        const pendingOp = this.getPendingOp();
        const recentAck = this.getRecentAck();
        const pendingOpSize = pendingOp ? getShareJsOpSize(pendingOp) : 0;

        let saved;
        if (inflightOp == null && pendingOp == null) {
            saved = true;
        } else if (inflightOp && inflightOp === this.oldInflightOp) {
            saved = false;
        } else if (pendingOp != null && recentAck && pendingOpSize < MAX_PENDING_OP_SIZE) {
            saved = true;
        } else {
            saved = false;
        }

        this.oldInflightOp = inflightOp;
        return saved;
    }

    bindToSocketEvents() {
        this.socket.on?.("otUpdateApplied", this.onUpdateAppliedHandler);
        this.socket.on?.("otUpdateError", this.onErrorHandler);
        this.socket.on?.("disconnect", this.onDisconnectHandler);
        if (!this.ideEventEmitter && !this.onProjectJoined) {
            this.socket.on?.("connect", this.onReconnectHandler);
        }
        if (this.onProjectJoined) {
            this.socket.on?.("project:joined", this.onProjectJoined);
        }
    }

    unBindFromSocketEvents() {
        this.socket.removeListener?.("otUpdateApplied", this.onUpdateAppliedHandler);
        this.socket.removeListener?.("otUpdateError", this.onErrorHandler);
        this.socket.removeListener?.("disconnect", this.onDisconnectHandler);
        if (!this.ideEventEmitter && !this.onProjectJoined) {
            this.socket.removeListener?.("connect", this.onReconnectHandler);
        }
        if (this.onProjectJoined) {
            this.socket.removeListener?.("project:joined", this.onProjectJoined);
        }
    }

    bindToEditorEvents() {
        if (typeof this.ideEventEmitter?.on === "function") {
            this.ideEventEmitter.on("project:joined", this.onReconnectHandler);
        }
    }

    unBindFromEditorEvents() {
        if (typeof this.ideEventEmitter?.off === "function") {
            this.ideEventEmitter.off("project:joined", this.onReconnectHandler);
        }
    }

    cancelLeave() {
        this.leaveCallbacks = [];
    }

    cancelJoin() {
        this.joinCallbacks = [];
    }

    callJoinCallbacks() {
        for (const callback of this.joinCallbacks) {
            callback();
        }
        this.joinCallbacks = [];
    }

    onUpdateApplied(update) {
        if (!this.doc) {
            return;
        }

        if (update?.doc != null && String(update.doc) !== String(this.doc_id)) {
            return;
        }

        this.doc.processUpdateFromServerInOrder(update);

        if (!this.wantToBeJoined) {
            this.leave();
        }
    }

    onDisconnect() {
        debugConsole.log("[onDisconnect] disconnecting");
        this.connected = false;
        this.joined = false;
        if (this.doc) {
            this.doc.updateConnectionState("disconnected");
        }
    }

    onReconnect() {
        this.connected = true;
        debugConsole.log("[onReconnect] reconnected (joined project)");
        if (!(this.wantToBeJoined || this.doc?.hasBufferedOps())) {
            return;
        }

        const epoch = ++this.joinLeaveEpoch;
        this.joinDoc((error) => {
            if (error) {
                this.onError(error);
                return;
            }
            this.doc?.updateConnectionState("ok");
            this.doc?.flushPendingOps();
            this.callJoinCallbacks();
        }, epoch);
    }

    joinDoc(callback, epoch = this.joinLeaveEpoch) {
        if (this.doc) {
            this.socket.emit(
                "joinDoc",
                this.doc_id,
                this.doc.getVersion(),
                {
                    encodeRanges: true,
                    supportsHistoryOT: true,
                    age: this.doc.getTimeSinceLastServerActivity(),
                },
                (error, docLines, version, updates, ranges, type = this.type) => {
                    if (error) {
                        callback?.(error);
                        return;
                    }
                    if (epoch !== this.joinLeaveEpoch) {
                        callback?.();
                        return;
                    }

                    this.joined = true;
                    this.doc.catchUp(updates);

                    if (this.doc.getType() !== type) {
                        callback?.(
                            new EditorError("ot type mismatch", {
                                got: type,
                                want: this.doc.getType(),
                            }),
                        );
                        return;
                    }

                    if (type === "sharejs-text-ot") {
                        this.decodeRanges(ranges);
                        this.catchUpRanges(ranges?.changes, ranges?.comments);
                    }
                    this.emit("ranges:update", this.getRanges());
                    callback?.();
                },
            );
            return;
        }

        this.socket.emit(
            "joinDoc",
            this.doc_id,
            {
                encodeRanges: true,
                supportsHistoryOT: true,
            },
            (error, docLines, version, updates, ranges, type = this.type) => {
                if (error) {
                    callback?.(error);
                    return;
                }
                if (epoch !== this.joinLeaveEpoch) {
                    callback?.();
                    return;
                }

                this.joined = true;
                if (type === "sharejs-text-ot") {
                    this.decodeRanges(ranges);
                }
                this.doc = new ShareJsDoc(this.doc_id, docLines, version, this.socket, {
                    type,
                    maxDocLength: this.maxDocLength,
                    globalEditorWatchdogManager: this.globalEditorWatchdogManager,
                    eventEmitter: this.ideEventEmitter,
                });
                this.doc.setTrackChangesUserId(this.track_changes_as);
                this.ranges = new RangesTracker(ranges?.changes, ranges?.comments);

                this.bindToShareJsDocEvents();

                if (updates?.length) {
                    this.doc.catchUp(updates);
                }

                if (this.cm6) {
                    this.doc.attachToCM6(this.cm6);
                    this.cm6.setTrackChangesUserId?.(this.track_changes_as);
                }

                this.emit("ranges:update", this.getRanges());
                callback?.();
            },
        );
    }

    leaveDoc(callback) {
        debugConsole.log("[leaveDoc] Sending leaveDoc request");
        this.socket.emit("leaveDoc", this.doc_id, (error) => {
            if (error) {
                callback?.(error);
                return;
            }

            this.joined = false;
            for (const leaveCallback of this.leaveCallbacks) {
                debugConsole.log("[_leaveDoc] Calling buffered callback", leaveCallback);
                leaveCallback(error);
            }
            this.leaveCallbacks = [];
            callback?.();
        });
    }

    cleanUp() {
        if (this.hasBufferedOps()) {
            debugConsole.log(`[cleanUp] Document (${this.doc_id}) has buffered ops, refusing to remove from openDocs`);
            return;
        }

        this.detachDoc(this.doc_id, this);
        this.unBindFromEditorEvents();
        this.unBindFromSocketEvents();
    }

    bindToShareJsDocEvents() {
        if (!this.doc) {
            return;
        }

        this.doc.on("error", (error, meta) => this.onError(error, meta));
        this.doc.on("externalUpdate", (update) => this.trigger("externalUpdate", update));
        this.doc.on("remoteop", (...ops) => this.trigger("remoteop", ...ops));
        this.doc.on("op:sent", (op) => {
            this.trigger("op:sent");
        });
        this.doc.on("op:acknowledged", (op) => {
            this.ideEventEmitter?.emit?.("ide:opAcknowledged", {
                doc_id: this.doc_id,
                op,
            });
            this.trigger("op:acknowledged");
        });
        this.doc.on("op:timeout", (update) => {
            this.trigger("op:timeout");
            this.onError(new Error("op timed out"));
        });
        this.doc.on("change", (ops, oldSnapshot, msg) => {
            if (!this.isHistoryOT()) {
                this.applyOpsToRanges(ops, msg);
            }
            if (this.docChangedTimeout) {
                window.clearTimeout(this.docChangedTimeout);
            }
            this.docChangedTimeout = window.setTimeout(() => {
                if (Array.isArray(ops) && ops.some((op) => isEditOperation(op))) {
                    window.dispatchEvent(new CustomEvent("doc:changed", { detail: { id: this.doc_id } }));
                    this.ideEventEmitter?.emit?.("doc:changed", {
                        doc_id: this.doc_id,
                    });
                }
            }, 50);
            this.trigger("change", ops, oldSnapshot, msg);
        });
        this.doc.on("ranges:dirty", (...args) => {
            this.trigger("ranges:dirty", ...args);
        });
        this.doc.on("flipped_pending_to_inflight", () => this.trigger("flipped_pending_to_inflight"));
        this.doc.on("saved", () => {
            if (this.docSavedTimeout) {
                window.clearTimeout(this.docSavedTimeout);
            }
            this.docSavedTimeout = window.setTimeout(() => {
                window.dispatchEvent(new CustomEvent("doc:saved", { detail: { id: this.doc_id } }));
                this.ideEventEmitter?.emit?.("doc:saved", {
                    doc_id: this.doc_id,
                });
            }, 50);
            this.trigger("saved");
        });
    }

    onError(error, meta = {}, editorContent) {
        meta.doc_id = this.doc_id;
        debugConsole.log("ShareJS error", error, meta);
        if (error?.message === "no project_id found on client") {
            debugConsole.log("ignoring error, will wait to join project");
            return;
        }
        if (error?.message === "socket_not_open") {
            debugConsole.log("ignoring transient socket error, will wait for reconnect");
            return;
        }
        if (this.doc) {
            this.doc.clearInflightAndPendingOps();
        }
        this.trigger("error", error, meta, editorContent);
        this.cleanUp();
    }

    applyOpsToRanges(ops, msg) {
        if (this.isHistoryOT() || !(this.ranges instanceof RangesTracker) || !Array.isArray(ops) || !ops.length) {
            return;
        }

        let previousIdSeed = null;
        let trackChangesAs = null;
        const isRemoteOp = msg != null;
        if (isRemoteOp && msg?.meta?.tc) {
            previousIdSeed = this.ranges.getIdSeed();
            this.ranges.setIdSeed(msg.meta.tc);
            trackChangesAs = msg.meta.user_id || null;
        } else if (!isRemoteOp && this.track_changes_as != null) {
            trackChangesAs = this.track_changes_as;
        }

        this.ranges.track_changes = trackChangesAs != null;
        for (const op of this.filterOps(ops)) {
            if (op?.i != null || op?.d != null || op?.c != null) {
                this.ranges.applyOp(op, { user_id: trackChangesAs });
            }
        }

        if (previousIdSeed != null) {
            this.ranges.setIdSeed(previousIdSeed);
        }

        if (isRemoteOp) {
            window.setTimeout(() => this.trigger("ranges:dirty"), 0);
            return;
        }

        this.trigger("ranges:dirty");
    }

    catchUpRanges(changes = [], comments = []) {
        if (this.isHistoryOT()) {
            this.trigger("ranges:redraw");
            return;
        }

        this.trigger("ranges:clear");

        if (!(this.ranges instanceof RangesTracker)) {
            this.ranges = new RangesTracker(changes, comments);
        } else {
            this.ranges.changes = Array.isArray(changes) ? changes : [];
            this.ranges.comments = Array.isArray(comments) ? comments : [];
        }

        this.ranges.track_changes = this.doc?.track_changes ?? false;

        for (const op of this.filterOps(this.getInflightOp() || [])) {
            if (op?.i != null || op?.d != null || op?.c != null) {
                if (this.doc?.track_changes_id_seeds?.inflight) {
                    this.ranges.setIdSeed(this.doc.track_changes_id_seeds.inflight);
                }
                this.ranges.applyOp(op, { user_id: this.track_changes_as });
            }
        }

        for (const op of this.filterOps(this.getPendingOp() || [])) {
            if (op?.i != null || op?.d != null || op?.c != null) {
                if (this.doc?.track_changes_id_seeds?.pending) {
                    this.ranges.setIdSeed(this.doc.track_changes_id_seeds.pending);
                }
                this.ranges.applyOp(op, { user_id: this.track_changes_as });
            }
        }

        this.trigger("ranges:redraw");
    }

    decodeRanges(ranges) {
        try {
            if (Array.isArray(ranges?.changes)) {
                for (const change of ranges.changes) {
                    if (isInsertOperation(change?.op)) {
                        change.op.i = decodeUtf8(change.op.i);
                    }
                    if (isDeleteOperation(change?.op)) {
                        change.op.d = decodeUtf8(change.op.d);
                    }
                }
            }

            if (Array.isArray(ranges?.comments)) {
                for (const comment of ranges.comments) {
                    if (comment?.op?.c != null) {
                        comment.op.c = decodeUtf8(comment.op.c);
                    }
                }
            }
        } catch (error) {
            debugConsole.error(error);
        }
    }

    filterOps(ops) {
        if (getMeta("ol-isRestrictedTokenMember")) {
            return ops.filter((op) => !isCommentOperation(op));
        }
        return ops;
    }
}

export default DocumentContainer;
